import { ArrowLeftOutlined, EditOutlined, FileTextOutlined } from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  Descriptions,
  Flex,
  Result,
  Space,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import {
  useShipmentsGetShipment,
  useShipmentsHistory,
} from '@/api/generated/endpoints/shipments/shipments';
import type { AllocationOut, ShipmentDetail, ShipmentItemOut } from '@/api/generated/model';
import { ApiProblem } from '@/api/http';
import { ExpiryTag } from '@/components/ExpiryTag';
import { QtyCell } from '@/components/qty';
import { ResolutionPopover } from '@/components/ResolutionPopover';
import { ShipmentStatusTag } from '@/components/StatusTag';
import { compareDecimals, formatDecimal } from '@/lib/decimal';
import { useIsMobile } from '@/lib/useIsMobile';
import { AcceptanceModal, type AcceptanceKind } from '@/pages/shipments/AcceptanceModal';
import { DraftEditor } from '@/pages/shipments/DraftEditor';
import { TransitionModal } from '@/pages/shipments/TransitionModal';
import { SIMPLE_TARGETS, type SimpleTarget } from '@/pages/shipments/transitions';

const DATE = 'DD.MM.YYYY';
const ACCEPTANCE: AcceptanceKind[] = ['accepted_full', 'accepted_partial', 'returned'];
const DATETIME = 'DD.MM.YYYY HH:mm';

/** Карточка отгрузки /shipments/:id: шапка, состав с резервом по партиям, история, действия. */
export function ShipmentPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const id = Number(useParams().shipmentId);
  const detail = useShipmentsGetShipment(id, { query: { enabled: Number.isInteger(id) } });
  const [editing, setEditing] = useState(false);
  const [target, setTarget] = useState<SimpleTarget | null>(null);
  const [acceptance, setAcceptance] = useState<AcceptanceKind | null>(null);
  const { message } = App.useApp();
  const mobile = useIsMobile();
  const back = (
    <Button
      type="text"
      icon={<ArrowLeftOutlined />}
      onClick={() => {
        void navigate('..');
      }}
    />
  );

  if (detail.error instanceof ApiProblem && detail.error.status === 404) {
    return <Result status="404" title={t('shipment.notFound')} extra={back} />;
  }
  const s = detail.data;
  const targets = s ? (SIMPLE_TARGETS[s.status] ?? []) : [];

  /** Кнопки действий: на компьютере — в заголовке, на телефоне — крупные во всю ширину. */
  function actions(large: boolean) {
    if (!s || editing) return [];
    const size = large ? ('large' as const) : undefined;
    return [
      ...(s.status === 'draft'
        ? [
            <Button
              key="edit"
              size={size}
              block={large}
              icon={<EditOutlined />}
              onClick={() => {
                setEditing(true);
              }}
            >
              {t('shipment.edit')}
            </Button>,
          ]
        : []),
      ...targets.map((to) => (
        <Button
          key={to}
          size={size}
          block={large}
          type={to === 'at_warehouse' || to === 'shipped' ? 'primary' : 'default'}
          danger={to === 'cancelled'}
          onClick={() => {
            setTarget(to);
          }}
        >
          {t(`transition.${to}`)}
        </Button>
      )),
      ...(s.status === 'shipped'
        ? ACCEPTANCE.map((kind) => (
            <Button
              key={kind}
              size={size}
              block={large}
              type={kind === 'accepted_full' ? 'primary' : 'default'}
              danger={kind === 'returned'}
              onClick={() => {
                setAcceptance(kind);
              }}
            >
              {t(`acceptance.${kind}`)}
            </Button>
          ))
        : []),
    ];
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        loading={detail.isPending}
        title={
          s && (
            <Space wrap>
              {back}
              {s.number}
              <ShipmentStatusTag status={s.status} clientName={s.client.brand ?? s.client.name} />
              {s.resolution && <ResolutionPopover resolution={s.resolution} />}
              {s.upd_document_id !== null && (
                <Tooltip title={t('shipment.fromUpd')}>
                  <FileTextOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              )}
            </Space>
          )
        }
        extra={s && !mobile && <Space wrap>{actions(false)}</Space>}
      >
        {s && mobile && (
          // Телефон (T-176): действия — крупными кнопками во всю ширину, их нажимают на ходу.
          <Flex vertical gap={8} style={{ marginBottom: 16 }}>
            {actions(true)}
          </Flex>
        )}
        {s && !editing && <Header shipment={s} />}
        {s && editing && (
          <DraftEditor
            shipment={s}
            onSaved={() => {
              setEditing(false);
              void message.success(t('shipment.saved'));
            }}
            onCancel={() => {
              setEditing(false);
            }}
          />
        )}
      </Card>
      {s && !editing && <Lines shipment={s} />}
      {s && <History shipmentId={s.id} />}
      {s && (
        <AcceptanceModal
          shipment={s}
          kind={acceptance}
          onClose={() => {
            setAcceptance(null);
          }}
          onDone={(next) => {
            void message.success(
              t('acceptance.done', { status: t(`status.shipment.${next.status}.label`) }),
            );
          }}
        />
      )}
      {s && (
        <TransitionModal
          shipment={s}
          target={target}
          onClose={() => {
            setTarget(null);
          }}
          onDone={(next) => {
            void message.success(
              t('transition.done', { status: t(`status.shipment.${next.status}.label`) }),
            );
          }}
        />
      )}
    </Space>
  );
}

function Header({ shipment: s }: { shipment: ShipmentDetail }) {
  const { t } = useTranslation();
  const nonZero = (v: string) => compareDecimals(v, '0') !== 0;
  return (
    <Descriptions size="small" column={{ xs: 1, md: 2, xl: 3 }}>
      <Descriptions.Item label={t('shipment.client')}>
        {s.client.brand ? `${s.client.brand} · ${s.client.name}` : s.client.name}
      </Descriptions.Item>
      <Descriptions.Item label={t('shipment.point')}>
        {s.delivery_point?.name ?? '—'}
      </Descriptions.Item>
      <Descriptions.Item label={t('shipment.total')}>
        <Space>
          {nonZero(s.total_kg) && <QtyCell value={s.total_kg} uom="kg" />}
          {nonZero(s.total_pcs) && <QtyCell value={s.total_pcs} uom="pcs" />}
          {nonZero(s.total_box) && <QtyCell value={s.total_box} uom="box" />}
        </Space>
      </Descriptions.Item>
      <Descriptions.Item label={t('shipment.createdAt')}>
        {dayjs(s.created_at).format(DATETIME)}
      </Descriptions.Item>
      <Descriptions.Item label={t('shipment.shipped')}>
        {s.shipped_at ? dayjs(s.shipped_at).format(DATETIME) : '—'}
      </Descriptions.Item>
      {s.closed_at && (
        <Descriptions.Item label={t('shipment.closed')}>
          {dayjs(s.closed_at).format(DATETIME)}
        </Descriptions.Item>
      )}
    </Descriptions>
  );
}

function Lines({ shipment: s }: { shipment: ShipmentDetail }) {
  const { t } = useTranslation();
  const closed = s.items.some((i) => i.qty_accepted !== null);
  const reserved = s.items.some((i) => i.allocations.length > 0);
  const mobile = useIsMobile();

  // Телефон: товар, количество и итог приёмки — в одной ячейке.
  const mobileColumns: TableColumnsType<ShipmentItemOut> = [
    {
      key: 'product',
      title: t('shipment.colProduct'),
      render: (_, i) => (
        <Space direction="vertical" size={2}>
          <Typography.Text strong>
            {i.line_no}. {i.product.name}
          </Typography.Text>
          <QtyCell value={i.qty} uom={i.product.base_uom} />
          {closed && (
            <Typography.Text type="secondary">
              {t('shipment.colAccepted')}:{' '}
              <QtyCell value={i.qty_accepted} uom={i.product.base_uom} />
              {' · '}
              {t('shipment.colReturned')}:{' '}
              <QtyCell value={i.qty_returned} uom={i.product.base_uom} />
            </Typography.Text>
          )}
        </Space>
      ),
    },
  ];

  const columns: TableColumnsType<ShipmentItemOut> = [
    { key: 'line', title: t('shipment.colLine'), dataIndex: 'line_no', width: 50 },
    { key: 'product', title: t('shipment.colProduct'), render: (_, i) => i.product.name },
    {
      key: 'qty',
      title: t('shipment.colQty'),
      align: 'right',
      width: 140,
      render: (_, i) => <QtyCell value={i.qty} uom={i.product.base_uom} />,
    },
    {
      key: 'source',
      title: t('shipment.colSource'),
      width: 240,
      render: (_, i) => <SourceCell item={i} />,
    },
    ...(closed
      ? [
          {
            key: 'accepted',
            title: t('shipment.colAccepted'),
            align: 'right' as const,
            width: 130,
            render: (_: unknown, i: ShipmentItemOut) => (
              <QtyCell value={i.qty_accepted} uom={i.product.base_uom} />
            ),
          },
          {
            key: 'returned',
            title: t('shipment.colReturned'),
            align: 'right' as const,
            width: 130,
            render: (_: unknown, i: ShipmentItemOut) => (
              <QtyCell value={i.qty_returned} uom={i.product.base_uom} />
            ),
          },
        ]
      : []),
  ];

  return (
    <Card
      title={t('shipment.lines')}
      extra={
        !reserved &&
        s.status === 'draft' && (
          <Typography.Text type="secondary">{t('shipment.notAllocated')}</Typography.Text>
        )
      }
    >
      <Table<ShipmentItemOut>
        rowKey="id"
        size="small"
        columns={mobile ? mobileColumns : columns}
        dataSource={s.items}
        pagination={false}
        expandable={{
          rowExpandable: (i) => i.allocations.length > 0,
          defaultExpandAllRows: true,
          expandedRowRender: (i) => <Allocations item={i} />,
        }}
      />
    </Card>
  );
}

/** Как строка пришла в УПД: «1 015 шт × 0,25 кг/шт» и откуда вес штуки. */
function SourceCell({ item: i }: { item: ShipmentItemOut }) {
  const { t } = useTranslation();
  if (i.source_qty === null) return <>—</>;
  const pcs = i.source_okei === '796';
  return (
    <Space direction="vertical" size={0}>
      <span>
        {pcs && i.kg_per_pcs
          ? t('shipment.sourceQuant', {
              qty: `${formatDecimal(i.source_qty)} ${t('uom.pcs')}`,
              kg: formatDecimal(i.kg_per_pcs),
            })
          : formatDecimal(i.source_qty)}
      </span>
      {i.kg_per_pcs_origin && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t(`shipment.quantOrigin.${i.kg_per_pcs_origin}`)}
        </Typography.Text>
      )}
    </Space>
  );
}

function Allocations({ item }: { item: ShipmentItemOut }) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const mobileColumns: TableColumnsType<AllocationOut> = [
    {
      key: 'batch',
      title: t('shipment.colBatch'),
      render: (_, a) => (
        <Space size={4} wrap>
          <span>№{a.batch_id}</span>
          <ExpiryTag expiresOn={a.expires_on} />
          <QtyCell value={a.qty} uom={item.product.base_uom} />
        </Space>
      ),
    },
  ];
  const columns: TableColumnsType<AllocationOut> = [
    {
      key: 'batch',
      title: t('shipment.colBatch'),
      dataIndex: 'batch_id',
      width: 90,
      render: (id: number) => `№${id}`,
    },
    {
      key: 'received',
      title: t('shipment.colReceived'),
      dataIndex: 'received_on',
      width: 110,
      render: (d: string) => dayjs(d).format(DATE),
    },
    {
      key: 'expires',
      title: t('shipment.colExpires'),
      width: 260,
      render: (_, a) => <ExpiryTag expiresOn={a.expires_on} />,
    },
    {
      key: 'qty',
      title: t('shipment.colAllocated'),
      align: 'right',
      render: (_, a) => <QtyCell value={a.qty} uom={item.product.base_uom} />,
    },
  ];
  return (
    <>
      <Typography.Text type="secondary">{t('shipment.allocations')}</Typography.Text>
      <Table<AllocationOut>
        rowKey="batch_id"
        size="small"
        columns={mobile ? mobileColumns : columns}
        dataSource={item.allocations}
        pagination={false}
      />
    </>
  );
}

function History({ shipmentId }: { shipmentId: number }) {
  const { t } = useTranslation();
  const history = useShipmentsHistory(shipmentId);
  return (
    <Card title={t('shipment.history')} loading={history.isPending}>
      <Timeline
        items={(history.data ?? []).map((h) => {
          const comment = h.payload?.comment;
          return {
            key: h.id,
            children: (
              <Space direction="vertical" size={0}>
                <Space wrap>
                  <ShipmentStatusTag status={h.to_status} />
                  <Typography.Text type="secondary">
                    {dayjs(h.created_at).format(DATETIME)} ·{' '}
                    {t('shipment.historyBy', { id: h.actor_id })}
                  </Typography.Text>
                </Space>
                {comment && <span>{comment}</span>}
              </Space>
            ),
          };
        })}
      />
      {history.data?.length === 0 && <Tag>—</Tag>}
    </Card>
  );
}
